import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Loader2, Send, MessageCircle, Mail, AlertTriangle, Users, X, HeartHandshake, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const NOTE_MAX = 600;

function whatsappTemplate(a) {
  const first = (a.full_name || "").trim().split(/\s+/)[0] || "there";
  const ref = (a.reference || "").toUpperCase();
  return [
    `Hi ${first}, this is the casting team at Ege Models (egemodels.com).`,
    ``,
    `We received your application (ref ${ref}) and really liked your photos — we'd love to meet you.`,
    ``,
    `Would you be free for a short 15–20 minute video call this week? Let us know which day and time suit you best.`,
    ``,
    `You'll also receive an email from info@egemodels.com confirming this message is really from us. We never ask for any payment.`,
    ``,
    `Talk soon,`,
    `Ege Models — Casting team`,
  ].join("\n");
}

function WhatsAppTemplate({ a }) {
  const [text, setText] = useState(() => whatsappTemplate(a));
  const [copied, setCopied] = useState(false);
  const areaRef = useRef(null);
  const digits = (a.phone || "").replace(/\D/g, "");
  const waHref = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : null;

  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // Fallback for browsers without async clipboard permission
      try {
        areaRef.current?.select();
        ok = document.execCommand("copy");
      } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      toast.success("WhatsApp message copied");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Couldn't copy — select the text and copy manually");
    }
  };

  return (
    <div data-testid="whatsapp-template" className="border border-emerald-200 bg-emerald-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="label-overline text-emerald-900 inline-flex items-center gap-1.5"><MessageCircle size={11}/> WhatsApp message</p>
        <span className="text-[11px] text-zinc-500">{digits ? a.phone : "no phone on file"}</span>
      </div>
      <Textarea
        ref={areaRef}
        data-testid="whatsapp-template-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="bg-white border-emerald-200 rounded-none min-h-[150px] text-[13px] leading-relaxed"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={copy}
          data-testid="whatsapp-template-copy"
          className="inline-flex items-center gap-1.5 border border-black bg-black text-white px-3 py-2 text-[11px] uppercase tracking-[0.16em] hover:bg-white hover:text-black transition-colors"
        >
          {copied ? <Check size={12}/> : <Copy size={12}/>} {copied ? "Copied" : "Copy text"}
        </button>
        {waHref ? (
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-template-open"
            className="inline-flex items-center gap-1.5 border border-black/20 px-3 py-2 text-[11px] uppercase tracking-[0.16em] hover:border-black transition-colors"
          >
            <ExternalLink size={12}/> Open in WhatsApp
          </a>
        ) : (
          <span className="text-[11px] text-zinc-500">Add a phone number to open WhatsApp directly.</span>
        )}
      </div>
    </div>
  );
}

const MODES = {
  invite: {
    icon: MessageCircle,
    overline: "Accept & invite to meet",
    path: "invite",
    description: (a, p) => (
      <>Emails <span className="text-black">{a.email}</span> that you reached out on WhatsApp
      {p?.whatsapp_number ? ` from ${p.whatsapp_number}` : ""}, explains next steps, and moves the application to <span className="text-black">invited</span>.</>
    ),
    placeholder: "e.g. We loved the natural light in your third photo — someone from our team will message you this afternoon.",
    hint: "Step 1: copy the WhatsApp message above (or open WhatsApp directly) and send it. Step 2: click send here — the email tells the applicant to look for your message and what to prepare for the intro call.",
    sendLabel: (a) => (a.invited_at ? "Send again" : "Send invitation"),
    toast: (r) => `Invitation sent to ${r.sent_to}${r.team_notified ? " · team notified" : " · team copy failed"}`,
    warning: (a) => a.invited_at && `Already invited ${fmtDateTime(a.invited_at)}${a.invited_by ? ` by ${a.invited_by}` : ""}${a.invite_count > 1 ? ` (${a.invite_count}×)` : ""}. Sending again will email the applicant a second time.`,
    allowSkipEmail: false,
  },
  decline: {
    icon: HeartHandshake,
    overline: "Decline · kind \"not right now\"",
    path: "decline",
    description: (a) => (
      <>Sends <span className="text-black">{a.email}</span> a warm, on-brand "not right now" email with re-apply tips, and moves the application to <span className="text-black">rejected</span>.</>
    ),
    placeholder: "e.g. Your digitals were lovely — we'd genuinely like to see you again next year once you've grown into your look.",
    hint: "The email is kind by default: it explains this isn't a judgement, keeps the door open, and includes the photo guide. Untick the box to decline silently (e.g. spam or duplicates).",
    sendLabel: (a, sendEmail) => (sendEmail ? "Send & decline" : "Decline without email"),
    toast: (r) => (r.emailed ? `Declined · email sent to ${r.sent_to}` : "Declined without email"),
    warning: (a) => a.declined_at && `Already declined ${fmtDateTime(a.declined_at)}${a.declined_by ? ` by ${a.declined_by}` : ""}${a.decline_emailed ? " (email sent)" : ""}.`,
    allowSkipEmail: true,
  },
};

/* One dialog for both applicant emails (invite / decline): live preview of the
   exact email, optional personal note, then send. Mount only while open so
   state is fresh per applicant. */
export default function OutreachDialog({ application: a, mode = "invite", onClose, onSent }) {
  const cfg = MODES[mode];
  const Icon = cfg.icon;
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoadingPreview(true);
      try {
        const r = await api.get(`/admin/applications/${a.id}/${cfg.path}/preview`, { params: { note } });
        if (!cancelled) { setPreview(r.data); setError(""); }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load the email preview");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [a.id, note, cfg.path]);

  const send = async () => {
    setSending(true);
    try {
      const body = cfg.allowSkipEmail ? { note, send_email: sendEmail } : { note };
      const r = await api.post(`/admin/applications/${a.id}/${cfg.path}`, body);
      toast.success(cfg.toast(r.data));
      onSent?.(r.data.application);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sending failed");
    } finally {
      setSending(false);
    }
  };

  const warning = cfg.warning(a);
  const notConfigured = preview && !preview.configured;
  const emailBlocked = sendEmail && (loadingPreview || !!error || notConfigured);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={`${mode}-dialog`}
        className="bg-white border-black/15 rounded-none max-w-6xl w-[96vw] text-[#0A0A0A] max-h-[92vh] overflow-y-auto p-0"
      >
        <div className="p-6 lg:p-8 border-b border-black/10">
          <DialogHeader className="space-y-2 text-left">
            <p className="label-overline text-zinc-500 inline-flex items-center gap-2">
              <Icon size={11}/> {cfg.overline} · {a.reference || "—"}
            </p>
            <DialogTitle className="font-serif text-3xl md:text-4xl font-light tracking-tighter leading-none">
              {a.full_name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {cfg.description(a, preview)}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 lg:p-8">
          <div className="lg:col-span-4 space-y-6">
            {warning && (
              <div data-testid={`${mode}-already-warning`} className="border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm flex gap-3">
                <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
                <p>{warning}</p>
              </div>
            )}
            {notConfigured && sendEmail && (
              <div data-testid={`${mode}-not-configured`} className="border border-red-300 bg-red-50 text-red-900 p-4 text-sm flex gap-3">
                <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
                <p>Email sending is not configured on the server. Add the email keys to the backend .env first.</p>
              </div>
            )}

            {cfg.allowSkipEmail && (
              <label className="flex items-start gap-3 text-sm cursor-pointer select-none border border-black/10 p-3">
                <input
                  type="checkbox"
                  data-testid={`${mode}-send-email-toggle`}
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="mt-0.5 accent-black"
                />
                <span>
                  <span className="block font-medium">Email the applicant</span>
                  <span className="block text-zinc-500 text-xs mt-0.5">Untick to decline silently — status still changes to rejected.</span>
                </span>
              </label>
            )}

            {mode === "invite" && <WhatsAppTemplate a={a}/>}

            <div className={sendEmail ? "" : "opacity-40 pointer-events-none"}>
              <label htmlFor={`${mode}-note`} className="label-overline text-zinc-500 block mb-2">Personal note · optional</label>
              <Textarea
                id={`${mode}-note`}
                data-testid={`${mode}-note-input`}
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder={cfg.placeholder}
                className="bg-white border-black/15 rounded-none min-h-[120px] text-sm"
              />
              <p className="text-[11px] text-zinc-400 mt-1 text-right">{note.length}/{NOTE_MAX}</p>
            </div>

            <div className="space-y-3 text-sm">
              <p className="label-overline text-zinc-500">What will be sent</p>
              <Row icon={<Mail size={13}/>} label="Applicant">
                <span data-testid={`${mode}-to`} className={`break-all ${sendEmail ? "" : "line-through text-zinc-400"}`}>{a.email}</span>
              </Row>
              {mode === "invite" && (
                <>
                  <Row icon={<Users size={13}/>} label="Team copy">
                    {preview?.team_recipients?.length ? (
                      <span data-testid={`${mode}-team-recipients`} className="break-all">{preview.team_recipients.join(", ")}</span>
                    ) : <span className="text-zinc-400">none configured</span>}
                  </Row>
                  <Row icon={<MessageCircle size={13}/>} label="WhatsApp">
                    <span>{preview?.whatsapp_number || "—"}</span>
                  </Row>
                </>
              )}
            </div>

            <div className="text-xs text-zinc-500 leading-relaxed border-t border-black/10 pt-4">{cfg.hint}</div>
          </div>

          <div className={`lg:col-span-8 ${sendEmail ? "" : "opacity-40"}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="label-overline text-zinc-500">Email preview</p>
              {loadingPreview && <Loader2 size={13} className="animate-spin text-zinc-400"/>}
            </div>
            {preview?.subject && (
              <p data-testid={`${mode}-subject`} className="text-sm mb-2 border border-black/10 bg-[#FAFAFA] px-3 py-2">
                <span className="text-zinc-500">Subject:</span> {preview.subject}
              </p>
            )}
            {error ? (
              <div className="border border-red-300 bg-red-50 text-red-900 p-4 text-sm">{error}</div>
            ) : (
              <iframe
                title="Email preview"
                data-testid={`${mode}-preview-frame`}
                srcDoc={preview?.html || ""}
                sandbox=""
                className="w-full h-[560px] border border-black/10 bg-[#EFEFEF]"
              />
            )}
          </div>
        </div>

        <div className="border-t border-black/10 bg-[#FAFAFA] p-6 lg:p-8 sticky bottom-0">
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid={`${mode}-cancel-btn`}
              className="label-overline text-xs text-zinc-700 hover:text-black inline-flex items-center gap-1 px-3 py-2"
            >
              <X size={12}/> Cancel
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending || emailBlocked}
              data-testid={`${mode}-send-btn`}
              className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
              {cfg.sendLabel(a, sendEmail)}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
      <span className="text-zinc-500 inline-flex items-center gap-1.5 pt-0.5">
        {icon}<span className="label-overline text-[10px]">{label}</span>
      </span>
      <span className="text-zinc-900">{children}</span>
    </div>
  );
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
